<template>
  <div class="question-container">
    <div class="questionnaire-multiple">

      <div v-if="inputType(question) === 'checkbox' && answerContainer.answers !== null">
        <span v-for="(option, index) in question.options.data" :key="index">
          <div v-if="!skipOption(option)">
            <label class="questionnaire-multiple-checkbox">
              <input
                style="display:none"
                @change="toggleChanged(option.id)"
                :class="inputClass(question)"
                :type="inputType(question)"
                :name="name(question)"
                :checked="answerContainer.answers[index].value"
              >
              <div class="questionnaire-multiple-checkbox-icon" :class="{ active: answerContainer.answers[index].value }" />
              <span :class="{ active: answerContainer.answers[index].value }">{{ option.text }}</span>
            </label>
          </div>
        </span>
      </div>

      <div v-if="inputType(question) === 'radio' && answerContainer.answers != null">
        <span v-for="(option, index) in question.options.data" :key="index">
          <div v-if="!skipOption(option)">
            <label class="questionnaire-multiple-radio">
              <input
                style="display:none"
                @change="toggleChanged(option.id)"
                :class="inputClass(question)"
                :type="inputType(question)"
                :name="name(question)"
                :checked="answerContainer.answers[index].value"
              >
              <div class="questionnaire-multiple-radio-icon" :class="{ active: answerContainer.answers[index].value }" />
              <span :class="{ active: answerContainer.answers[index].value }">{{ option.text }}</span>
            </label>
          </div>
        </span>
      </div>

      <span
        class="questionnaire-error-text"
        v-if="$v.checkedButtons.$error && question.friendlyName.ranged"
      >
        U dient{{this.isOptional ? " geen of" : ""}} minimaal {{$v.checkedButtons.$params.between.min}} en maximaal {{$v.checkedButtons.$params.between.max}} opties te kiezen.
      </span>
      <span
        class="questionnaire-error-text"
        v-if="$v.checkedButtons.$error && !question.friendlyName.ranged"
      >
        U dient minimaal 1 optie te kiezen.
      </span>
      <statement-field
        v-if="question.typeable !== 'undefined' && question.typeable.statement"
        @updated="statementUpdated"
      />
    </div>
  </div>
</template>

<script>
import {
  required,
  minLength,
  between,
  requiredUnless
} from 'vuelidate/lib/validators'
import { EventBus } from '../EventBus'
import StatementField from './StatementField'

export default {
  name: 'MultipleChoiceQuestion',

  components: {
    StatementField
  },

  props: {
    question: {
      type: Object,
      default: null
    }
  },

  validations () {
    const alwaysOK = { OK: () => true }

    if (this.isOptional) {
      return {
        checkedButtons: {
          between:
            this.checkedButtons === 0
              ? alwaysOK
              : between(this.minCheckedButtons, this.maxCheckedButtons)
        }
      }
    } else {
      return {
        checkedButtons: {
          between: between(this.minCheckedButtons, this.maxCheckedButtons)
        }
      }
    }
  },

  computed: {
    computedStatement () {
      return this.statement
    },
    isOptional () {
      return !this.question.typeable.required
    }
  },

  watch: {
    optionFilter (newFilter) {
      let that = this
      this.answerContainer.answers.forEach(function (answer, index) {
        if (newFilter.indexOf(answer.answerId) !== -1) {
          that.answerContainer.answers[index].value = false
        }
      })
    },
    selectedOption (newOption) {
      let that = this

      this.answerContainer.answers.forEach(function (answer, index) {
        that.answerContainer.answers[index].value =
          answer.answerId === newOption
      })
      this.answerContainerUpdated()
    }
  },

  methods: {
    statementUpdated (newStatement) {
      this.statement = newStatement
      this.$emit('statementUpdated', {
        questionId: this.question.id,
        statement: this.statement
      })
    },
    fetchFilter (getFilter) {
      this.optionFilter = getFilter(this.question)
    },
    toggleChanged (id) {
      let that = this
      this.answerContainer.answers.forEach(function (answer, index) {
        if (answer.answerId === id) {
          that.answerContainer.answers[index].value = !answer.value
        } else if (!that.question.friendlyName.ranged) {
          answer.value = false
        }
      })

      this.answerContainerUpdated()
    },
    getDropdownOptions () {
      var options = []

      options.push({
        text: this.question.text + (this.question.typeable.required ? '*' : ''),
        value: -1
      })

      this.question.options.data.forEach(option => {
        options.push({ text: option.text, value: option.id })
      })

      return options
    },
    inputClass (question) {
      return 'questionnaire' + question.friendlyName.ranged
        ? 'Checkbox'
        : 'Radio' + 'Button'
    },

    inputType (question) {
      return question.friendlyName.ranged ? 'checkbox' : 'radio'
    },

    name (question) {
      return 'question' + question.id.toString()
    },
    skipOption (option) {
      var skip = false

      this.optionFilter.forEach(function (filteredOption) {
        if (option.value == filteredOption) {
          skip = true
        }
      })

      return skip
    },
    answerContainerUpdated () {
      let parsedAnswer = {
        id: this.question.id,
        answer: null,
        ranged: this.question.friendlyName.ranged,
        valid: false
      }

      if (this.question.friendlyName.ranged) {
        parsedAnswer.answer = {
          questionId: this.question.id,
          answer: [],
          statement: this.statement
        }
        let that = this
        this.answerContainer.answers.forEach(function (answer) {
          if (answer.value === true) {
            parsedAnswer.answer.answer.push({ answer: answer.answerId })
          }
        })
        this.checkedButtons = parsedAnswer.answer.answer.length
      } else {
        let that = this

        this.answerContainer.answers.forEach(function (answer) {
          if (answer.value === true) {
            parsedAnswer.answer = {
              questionId: that.question.id,
              answer: answer.answerId,
              statement: that.statement
            }
          }
        })

        this.checkedButtons = parsedAnswer.answer == null ? 0 : 1
      }
      this.$v.$touch()
      parsedAnswer.valid = !this.$v.$invalid

      this.$emit('updated', parsedAnswer)
    }
  },

  data () {
    return {
      statement: null,
      answerContainer: { questionId: null, answers: null },
      optionFilter: [],
      checkedButtons: 0,
      minCheckedButtons: 1,
      maxCheckedButtons: 1000,
      selectedOption: -1
    }
  },

  mounted () {
    this.answerContainer.answers = []
    this.answerContainer.questionId = this.question.id
    let that = this
    this.question.options.data.forEach(function (option) {
      that.answerContainer.answers.push({ answerId: option.id, value: false })
    })

    if (this.question.friendlyName.ranged) {
      this.minCheckedButtons = this.question.questionable.data.min
      this.maxCheckedButtons = this.question.questionable.data.max
    }

    EventBus.$on('questionAnswered', getFilter => {
      that.optionFilter = getFilter(that.question)
    })
  },
  created () {
    EventBus.$on('validate', this.answerContainerUpdated)
    EventBus.$on('questionAnswered', this.fetchFilter)
  },
  beforeDestroy () {
    EventBus.$off('questionAnswered', this.fetchFilter)
    EventBus.$off('validate', this.answerContainerUpdated)
  }
}
</script>
